import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import iconFaxDownloadDark900 from '~/assets/icons-light-theme/fax-download-icon.svg'
import iconFirstPageDark900 from '~/assets/icons-light-theme/fax-first-page-icon.svg'
import iconLastPageDark900 from '~/assets/icons-light-theme/fax-last-page-icon.svg'
import iconNextPageDark900 from '~/assets/icons-light-theme/fax-next-page-icon.svg'
import iconPrevPageDark900 from '~/assets/icons-light-theme/fax-prev-page-icon.svg'
import iconFaxPrintDark900 from '~/assets/icons-light-theme/fax-print-icon.svg'
import iconFaxRotateDark900 from '~/assets/icons-light-theme/fax-rotate-icon.svg'
import iconFaxShareDark900 from '~/assets/icons-light-theme/fax-share-icon.svg'
import { useUserState } from '~/atoms/UserState'
import { Text } from '~/components/core'
import { ImageIcon } from '~/components/ImageIcon'
import { light } from '~/constants/theme'

export const PDFToolbar = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  background: rgba(250, 250, 252, 0.6);
  border-top: none;
  border-radius: 0px 0px 8px 8px;
  padding: 14px 20px 16px 8px;
`

export const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`

export const IconWrapper = styled.button`
  margin-left: 24px;
  display: flex;
  cursor: pointer;
  height: 100%;
  align-items: center;
  padding: 0 12px 0 12px;
  border-radius: 14px;

  &:focus,
  &:hover {
    background: #e5e5ee;
  }

  &:active {
    background: #d3d3e2;
  }
`

export const ActionIcon = styled.button`
  margin-left: 12px;
  cursor: pointer;
  height: 28px;
  width: 28px;
  padding: 6px;
  border-radius: 14px;

  &:focus,
  &:hover {
    background: #e5e5ee;
  }

  &:active {
    background: #d3d3e2;
  }
`

interface PdfToolbarProps {
  pages: number
  pageNumber: number
  onPageNumberChanged: (delta: number) => void
  onOpenInTab: () => void
  onDownload: () => void
  onRotate: () => void
  onPrint: () => void
}

export const PdfToolbar = ({
  pages,
  pageNumber,
  onPageNumberChanged,
  onOpenInTab,
  onDownload,
  onRotate,
  onPrint,
}: PdfToolbarProps) => {
  const { userRoles } = useUserState()
  const isPortalLimited = userRoles?.roles?.some(
    (role) => role.type === 'Portal Limited',
  )
  const { t } = useTranslation()

  return (
    <PDFToolbar>
      <ToolbarSection>
        <IconWrapper onClick={() => onPrint()}>
          <ImageIcon icon={iconFaxPrintDark900} width={16} height={16} cursor />
          <Text
            text={t('pdfToolbar.print')}
            color={light.dark900}
            style={{
              marginLeft: '8px',
              fontSize: '14px',
              userSelect: 'none',
            }}
          />
        </IconWrapper>
        {isPortalLimited ? null : (
          <IconWrapper onClick={() => onDownload()}>
            <ImageIcon icon={iconFaxDownloadDark900} width={16} height={16} />
            <Text
              text={t('pdfToolbar.download')}
              color={light.dark900}
              style={{
                marginLeft: '8px',
                fontSize: '14px',
                userSelect: 'none',
              }}
            />
          </IconWrapper>
        )}

        <IconWrapper onClick={() => onOpenInTab()}>
          <ImageIcon icon={iconFaxShareDark900} width={16} height={16} />
          <Text
            text={t('pdfToolbar.openInNewTab')}
            color={light.dark900}
            style={{
              marginLeft: '8px',
              fontSize: '14px',
              userSelect: 'none',
            }}
          />
        </IconWrapper>
      </ToolbarSection>

      <ToolbarSection>
        {pages > 1 && (
          <>
            <Text
              text={t('toolbar.page')}
              color={light.dark900}
              style={{
                fontSize: '14px',
                userSelect: 'none',
                paddingRight: '4px',
              }}
            />
            <Text
              text={pageNumber.toString()}
              color={light.dark900}
              style={{
                fontSize: '14px',
                userSelect: 'none',
              }}
            />
            <Text
              text="/"
              color={light.dark900}
              style={{
                marginLeft: '2px',
                marginRight: '2px',
                fontSize: '14px',
                userSelect: 'none',
              }}
            />
            <Text
              text={pages.toString()}
              color={light.dark900}
              style={{
                fontSize: '14px',
                userSelect: 'none',
              }}
            />
            <ActionIcon
              onClick={() => {
                window.gtag('event', 'fax_preview_first_pg', {})
                onPageNumberChanged(-(pageNumber - 1))
              }}
            >
              <ImageIcon icon={iconFirstPageDark900} width={16} height={16} />
            </ActionIcon>
            <ActionIcon
              onClick={() => {
                window.gtag('event', 'fax_preview_prev_pg', {})
                onPageNumberChanged(-1)
              }}
            >
              <ImageIcon icon={iconPrevPageDark900} width={16} height={16} />
            </ActionIcon>
            <ActionIcon
              onClick={() => {
                window.gtag('event', 'fax_preview_next_pg', {})
                onPageNumberChanged(1)
              }}
            >
              <ImageIcon icon={iconNextPageDark900} width={16} height={16} />
            </ActionIcon>
            <ActionIcon
              onClick={() => {
                window.gtag('event', 'fax_preview_last_pg', {})
                onPageNumberChanged(pages - pageNumber)
              }}
            >
              <ImageIcon icon={iconLastPageDark900} width={16} height={16} />
            </ActionIcon>
          </>
        )}
        <ActionIcon onClick={() => onRotate()}>
          <ImageIcon icon={iconFaxRotateDark900} width={16} height={16} />
        </ActionIcon>
      </ToolbarSection>
    </PDFToolbar>
  )
}
